import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Multi-select for solution slugs. Fetches /api/solutions to build options.
 * Props:
 *  - value: string[] (selected slugs)
 *  - onChange: (slugs: string[]) => void
 *  - max: default 999
 */
export default function SolutionsMultiSelect({ value = [], onChange, max = 999 }) {
  const [open, setOpen] = useState(false);
  const [solutions, setSolutions] = useState([]);
  const selected = Array.isArray(value) ? value : [];

  useEffect(() => {
    axios.get(`${API}/solutions`).then(r => setSolutions(r.data || [])).catch(() => {});
  }, []);

  const toggle = (slug) => {
    if (selected.includes(slug)) {
      onChange(selected.filter(s => s !== slug));
    } else {
      if (selected.length >= max) return;
      onChange([...selected, slug]);
    }
  };

  return (
    <div className="relative" data-testid="solutions-multiselect">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white hover:border-[#0EA5E9] transition-colors"
      >
        <span className="truncate text-left">
          {selected.length === 0 ? (
            <span className="text-slate-400">Elige una o más soluciones…</span>
          ) : (
            <span className="text-slate-700">{selected.length} solución{selected.length !== 1 ? 'es' : ''} seleccionada{selected.length !== 1 ? 's' : ''}</span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map(s => {
            const sol = solutions.find(x => x.slug === s);
            return (
              <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[11px] font-semibold" data-testid={`solution-tag-${s}`}>
                {sol?.name || s}
                <button type="button" onClick={() => toggle(s)} className="hover:text-red-600 ml-0.5" aria-label="Quitar">×</button>
              </span>
            );
          })}
        </div>
      )}
      {open && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl max-h-64 overflow-y-auto">
          {solutions.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">Cargando soluciones…</div>
          ) : (
            solutions.map(sol => {
              const active = selected.includes(sol.slug);
              return (
                <button
                  type="button"
                  key={sol.slug}
                  onClick={() => toggle(sol.slug)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 ${active ? 'bg-sky-50' : ''}`}
                  data-testid={`solution-option-${sol.slug}`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${active ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-slate-300'}`}>
                    {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </span>
                  <span className="flex-1 text-slate-700">{sol.name}</span>
                  <span className="text-[10px] text-slate-400">{sol.slug}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
